import { Component } from '@theme/component';
import { morphSection } from '@theme/section-renderer';
import { DiscountUpdateEvent } from '@theme/events';
import { fetchConfig } from '@theme/utilities';
import { cartPerformance } from '@theme/performance';

if (!window.__cartDiscountDraft) {
  window.__cartDiscountDraft = {};
}

class CartDiscount extends Component {
  requiredRefs = [
    'cartDiscountError',
    'cartDiscountErrorDiscountCode',
    'cartDiscountErrorShipping',
  ];

  #activeFetch = null;
  #mutationObserver = null;
  #restoreTimeout = null;
  #isRestoring = false;

  get draftKey() {
    return this.dataset.sectionId || 'cart-discount';
  }

  connectedCallback() {
    super.connectedCallback?.();

    const input = this.querySelector('.cart-discount__input');
    if (input instanceof HTMLInputElement) {
      const draft = window.__cartDiscountDraft[this.draftKey];
      const appliedPromoCode = sessionStorage.getItem('appliedPromoCode');

      if (!appliedPromoCode && typeof draft === 'string' && draft.length > 0) {
        input.value = draft;
      }

      input.addEventListener('input', () => {
        const value = input.value;
        if (value) {
          window.__cartDiscountDraft[this.draftKey] = value;
        } else {
          delete window.__cartDiscountDraft[this.draftKey];
        }
      });
    }

    this.#restoreAppliedPromoState();

    this.#mutationObserver = new MutationObserver(() => {
      if (this.#isRestoring) return;

      if (this.#restoreTimeout) {
        clearTimeout(this.#restoreTimeout);
      }

      this.#restoreTimeout = setTimeout(() => {
        if (this.#isRestoring) return;

        const input = this.querySelector('.cart-discount__input');
        if (input instanceof HTMLInputElement && !input.readOnly) {
          if (!input.value) {
            const draft = window.__cartDiscountDraft[this.draftKey];
            if (typeof draft === 'string' && draft.length > 0) {
              input.value = draft;
            }
          }
        }

        this.#restoreAppliedPromoState();
      }, 100);
    });

    this.#mutationObserver.observe(this, {
      childList: true,
      subtree: true,
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();

    if (this.#restoreTimeout) {
      clearTimeout(this.#restoreTimeout);
      this.#restoreTimeout = null;
    }

    if (this.#mutationObserver) {
      this.#mutationObserver.disconnect();
      this.#mutationObserver = null;
    }
  }

  #createAbortController() {
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }

    const abortController = new AbortController();
    this.#activeFetch = abortController;
    return abortController;
  }

  #restoreAppliedPromoState() {
    if (this.#isRestoring) return;

    const appliedPromoCode = sessionStorage.getItem('appliedPromoCode');
    const appliedPromoDiscount = sessionStorage.getItem('appliedPromoDiscount');
    if (!appliedPromoCode) return;

    const promoCode = this.dataset.promoCode?.trim();
    if (!promoCode) return;

    if (appliedPromoCode.toLowerCase() !== promoCode.toLowerCase()) return;

    const successWrapper = this.querySelector('[data-promo-success]');
    const successMessage = this.querySelector('[data-success-message]');
    const discountInput = this.querySelector('.cart-discount__input');
    const discountButton = this.querySelector('.cart-discount__button');

    if (
      !(successWrapper instanceof HTMLElement) ||
      !(successMessage instanceof HTMLElement) ||
      !(discountInput instanceof HTMLInputElement) ||
      !(discountButton instanceof HTMLButtonElement)
    ) {
      return;
    }

    this.#isRestoring = true;

    const template =
      this.dataset.promoSuccessMessage ||
      'Gratulujeme! Dobili ste {discount}% zľavu.';

    const message = template.replace('{discount}', appliedPromoDiscount || '');
    successMessage.textContent = message;
    successWrapper.style.display = 'block';

    discountInput.value = appliedPromoCode;
    discountInput.readOnly = true;
    discountInput.style.opacity = '0.7';

    discountButton.type = 'button';
    discountButton.disabled = true;
    discountButton.style.pointerEvents = 'none';
    discountButton.style.cursor = 'not-allowed';

    discountButton.onclick = (e) => {
      e.preventDefault();
      e.stopPropagation();

      sessionStorage.removeItem('appliedPromoCode');
      sessionStorage.removeItem('appliedPromoDiscount');

      successWrapper.style.display = 'none';
      discountInput.readOnly = false;
      discountInput.style.opacity = '1';
      discountInput.value = '';

      discountButton.type = 'submit';
      discountButton.disabled = false;
      discountButton.style.pointerEvents = 'auto';
      discountButton.style.cursor = 'pointer';
      discountButton.onclick = null;
    };

    setTimeout(() => {
      this.#isRestoring = false;
    }, 50);
  }

  applyDiscount = async (event) => {
    const {
      cartDiscountError,
      cartDiscountErrorDiscountCode,
      cartDiscountErrorShipping,
    } = this.refs;

    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;

    const discountCode = form.querySelector('input[name="discount"]');
    if (
      !(discountCode instanceof HTMLInputElement) ||
      typeof this.dataset.sectionId !== 'string'
    ) {
      return;
    }

    const discountCodeValue = discountCode.value.trim();
    if (!discountCodeValue) {
      return;
    }

    const promoCode = this.dataset.promoCode?.trim();

    if (
      promoCode &&
      discountCodeValue.toLowerCase() === promoCode.toLowerCase()
    ) {
      sessionStorage.setItem('appliedPromoCode', discountCodeValue);
      sessionStorage.setItem(
        'appliedPromoDiscount',
        this.dataset.promoDiscount || '0'
      );

      cartDiscountError.classList.add('hidden');
      cartDiscountErrorDiscountCode.classList.add('hidden');
      cartDiscountErrorShipping.classList.add('hidden');

      delete window.__cartDiscountDraft[this.draftKey];

      this.#restoreAppliedPromoState();
      return;
    }

    const abortController = this.#createAbortController();

    try {
      const existingDiscounts = this.#existingDiscounts();
      if (existingDiscounts.includes(discountCodeValue)) return;

      cartDiscountError.classList.add('hidden');
      cartDiscountErrorDiscountCode.classList.add('hidden');
      cartDiscountErrorShipping.classList.add('hidden');

      const config = fetchConfig('json', {
        body: JSON.stringify({
          discount: [...existingDiscounts, discountCodeValue].join(','),
          sections: [this.dataset.sectionId],
        }),
      });

      const response = await fetch(Theme.routes.cart_update_url, {
        ...config,
        signal: abortController.signal,
      });

      const data = await response.json();

      if (
        data.discount_codes.find((discount) => {
          return (
            discount.code.toLowerCase() === discountCodeValue.toLowerCase() &&
            discount.applicable === false
          );
        })
      ) {
        discountCode.value = '';
        delete window.__cartDiscountDraft[this.draftKey];
        this.#handleDiscountError('discount_code');
        return;
      }

      const newHtml = data.sections[this.dataset.sectionId];
      const parsedHtml = new DOMParser().parseFromString(newHtml, 'text/html');
      const section = parsedHtml.getElementById(
        `shopify-section-${this.dataset.sectionId}`
      );
      const discountCodes =
        section?.querySelectorAll('.cart-discount__pill') || [];

      if (section) {
        const codes = Array.from(discountCodes)
          .map((element) =>
            element instanceof HTMLLIElement
              ? element.dataset.discountCode
              : null
          )
          .filter(Boolean);

        if (
          codes.length === existingDiscounts.length &&
          codes.every((code) => existingDiscounts.includes(code)) &&
          data.discount_codes.find((discount) => {
            return (
              discount.code.toLowerCase() === discountCodeValue.toLowerCase() &&
              discount.applicable === true
            );
          })
        ) {
          this.#handleDiscountError('shipping');
          discountCode.value = '';
          delete window.__cartDiscountDraft[this.draftKey];
          return;
        }
      }

      document.dispatchEvent(new DiscountUpdateEvent(data, this.id));
      morphSection(this.dataset.sectionId, newHtml);
    } catch (error) {
    } finally {
      this.#activeFetch = null;
      cartPerformance.measureFromEvent('discount-update:user-action', event);
    }
  };

  removeDiscount = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    if (
      (event instanceof KeyboardEvent && event.key !== 'Enter') ||
      !(event instanceof MouseEvent) ||
      !(event.target instanceof HTMLElement) ||
      typeof this.dataset.sectionId !== 'string'
    ) {
      return;
    }

    const pill = event.target.closest('.cart-discount__pill');
    if (!(pill instanceof HTMLLIElement)) return;

    const discountCode = pill.dataset.discountCode;
    if (!discountCode) return;

    const existingDiscounts = this.#existingDiscounts();
    const index = existingDiscounts.indexOf(discountCode);
    if (index === -1) return;

    existingDiscounts.splice(index, 1);

    const abortController = this.#createAbortController();

    try {
      const config = fetchConfig('json', {
        body: JSON.stringify({
          discount: existingDiscounts.join(','),
          sections: [this.dataset.sectionId],
        }),
      });

      const response = await fetch(Theme.routes.cart_change_url, {
        ...config,
        signal: abortController.signal,
      });

      const data = await response.json();

      document.dispatchEvent(new DiscountUpdateEvent(data, this.id));
      morphSection(
        this.dataset.sectionId,
        data.sections[this.dataset.sectionId]
      );
    } catch (error) {
    } finally {
      this.#activeFetch = null;
    }
  };

  #handleDiscountError(type) {
    const {
      cartDiscountError,
      cartDiscountErrorDiscountCode,
      cartDiscountErrorShipping,
    } = this.refs;
    const target =
      type === 'discount_code'
        ? cartDiscountErrorDiscountCode
        : cartDiscountErrorShipping;
    cartDiscountError.classList.remove('hidden');
    target.classList.remove('hidden');
  }

  #existingDiscounts() {
    const discountCodes = [];
    const discountPills = this.querySelectorAll('.cart-discount__pill');
    for (const pill of discountPills) {
      if (
        pill instanceof HTMLLIElement &&
        typeof pill.dataset.discountCode === 'string'
      ) {
        discountCodes.push(pill.dataset.discountCode);
      }
    }

    return discountCodes;
  }
}

if (!customElements.get('cart-discount-component')) {
  customElements.define('cart-discount-component', CartDiscount);
}

document.addEventListener(
  'submit',
  (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    if (!form.matches('.cart-discount__form')) return;

    event.preventDefault();
    event.stopPropagation();

    const component = form.closest('cart-discount-component');
    if (component && typeof component.applyDiscount === 'function') {
      component.applyDiscount(event);
    }
  },
  true
);
